<script lang="ts">
  /**
   * InlineField の multi-select 派生。
   * クリックで checkbox 群を展開、 各 checkbox トグルで即 commit。
   * panel 外をクリックすると閉じる (旧 InlineField.svelte の multi_select 挙動と同じ)。
   *
   * 旧の v2 UI は kind: 'multi_select' を「checkbox トグルで即 PATCH」 として
   * 扱っていた。 新実装の v1 では保存ボタン方式を試したが、 他の InlineField
   * (text / select / number) と挙動が不一致で体験が悪かったため、 旧と同じく
   * 即 commit に揃える。 panel は外クリックまで開きっぱなしで、 そこは旧
   * InlineField と差がある (旧は常時 panel、 新は chip → panel の切替) が、
   * 「クリックして編集」 という編集モード概念は他フィールドと共通。
   */
  interface SelectOption {
    value: string;
    label: string;
  }

  interface Props {
    value: string[] | null;
    options: SelectOption[];
    /** チェック変更ごとに呼ばれる。 値が空配列なら caller が `null` 化を判断する。 */
    onSave: (next: string[]) => void | Promise<void>;
    locked?: boolean;
    disabled?: boolean;
    label?: string;
    /**
     * クリック前に候補が分かる opt-in 表示。 既定 'default' は従来挙動
     * (chip クリックで panel を開く)。 既存呼び出し側は未指定で不変。
     * - 'compact': 候補を checkbox/chips として常時表示し、 トグルで即 commit。
     * - 'large': 選択済み chip or `未選択` + `選択` affordance。 クリックで panel。
     * `disabled` / `locked` のときは affordance を出さず plain な readonly 表示に倒す。
     */
    affordance?: 'default' | 'compact' | 'large';
    /** 値が空のとき表示するラベル (compact 以外)。 既定 `未選択`。 */
    emptyLabel?: string;
  }

  let {
    value,
    options,
    onSave,
    locked = false,
    disabled = false,
    label,
    affordance = 'default',
    emptyLabel = '未選択',
  }: Props = $props();

  let editing = $state(false);
  let saving = $state(false);
  let savedAt = $state<number | null>(null);
  let error = $state<string | null>(null);
  let panelEl: HTMLDivElement | null = $state(null);

  const canEdit = $derived(!locked && !disabled);

  function startEdit(): void {
    if (!canEdit || saving) return;
    editing = true;
  }

  function closePanel(): void {
    editing = false;
    error = null;
  }

  async function toggle(val: string): Promise<void> {
    if (!canEdit || saving) return;
    const current = Array.isArray(value) ? value : [];
    const next = current.includes(val)
      ? current.filter((v) => v !== val)
      : [...current, val];
    saving = true;
    error = null;
    try {
      await onSave(next);
      savedAt = Date.now();
      setTimeout(() => {
        if (savedAt && Date.now() - savedAt >= 1900) savedAt = null;
      }, 2000);
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      saving = false;
    }
  }

  function labelOf(v: string): string {
    return options.find((o) => o.value === v)?.label ?? v;
  }

  function displayLabel(): string {
    const arr = Array.isArray(value) ? value : [];
    if (arr.length === 0) return '';
    return arr.map((v) => labelOf(v)).join(' / ');
  }

  const selectedValues = $derived(Array.isArray(value) ? value : []);

  /// panel 外クリックを検出して閉じる。 mousedown phase で捕捉することで、
  /// 「checkbox を押した直後に panel が閉じて click event が消える」 ような
  /// 競合を避ける。
  function handleDocumentMouseDown(ev: MouseEvent): void {
    if (!editing || !panelEl) return;
    const target = ev.target as Node | null;
    if (target && !panelEl.contains(target)) {
      closePanel();
    }
  }

  $effect(() => {
    if (editing) {
      document.addEventListener('mousedown', handleDocumentMouseDown, true);
      return () => {
        document.removeEventListener('mousedown', handleDocumentMouseDown, true);
      };
    }
  });
</script>

<div class="inline-field" class:saving class:locked class:disabled>
  {#if label}<span class="field-label">{label}</span>{/if}
  <span
    class="inline-field-control"
    title={locked ? '項目定義でロックされています' : undefined}
  >
    {#if !canEdit}
      {#if !value || value.length === 0}
        <span class="muted">-</span>
      {:else}
        <span>{displayLabel()}</span>
      {/if}
      {#if locked}<span class="lock-mark" aria-label="ロック">🔒</span>{/if}
    {:else if affordance === 'compact'}
      <!-- compact: 候補を checkbox/chips として常時表示し、 クリック前に
           候補が分かるようにする。 panel は開かず、 トグルで即 commit。 -->
      <div class="chips chips-compact" role="group">
        {#each options as opt (opt.value)}
          <label class="chip" class:chip-selected={selectedValues.includes(opt.value)}>
            <input
              type="checkbox"
              checked={selectedValues.includes(opt.value)}
              onchange={() => toggle(opt.value)}
              disabled={saving}
            />
            {opt.label}
          </label>
        {/each}
      </div>
    {:else if editing}
      <div class="multi-edit" role="group" bind:this={panelEl}>
        <div class="chips">
          {#each options as opt (opt.value)}
            <label class="chip">
              <input
                type="checkbox"
                checked={(value ?? []).includes(opt.value)}
                onchange={() => toggle(opt.value)}
                disabled={saving}
              />
              {opt.label}
            </label>
          {/each}
        </div>
      </div>
    {:else if affordance === 'large'}
      <!-- large: 選択済みを chip 列で表示し、 `選択` affordance で multi-select と
           分かるようにする。 候補は常時展開せず、 クリックで panel を開く。 -->
      <button
        type="button"
        class="inline-display ms-large"
        title="クリックして編集"
        onclick={startEdit}
      >
        {#if selectedValues.length === 0}
          <span class="muted">{emptyLabel}</span>
        {:else}
          <span class="ms-static-chips">
            {#each selectedValues as v (v)}
              <span class="ms-static-chip">{labelOf(v)}</span>
            {/each}
          </span>
        {/if}
        <span class="ms-select-hint" aria-hidden="true">選択 ▾</span>
      </button>
    {:else}
      <button
        type="button"
        class="inline-display"
        title="クリックして編集"
        onclick={startEdit}
      >
        {#if !value || value.length === 0}
          <span class="muted">-</span>
        {:else}
          {displayLabel()}
        {/if}
      </button>
    {/if}
    {#if saving}
      <span class="status-note small muted">保存中…</span>
    {:else if savedAt}
      <span class="status-note small saved">保存しました</span>
    {/if}
    {#if error}
      <span class="status-note small error-msg">{error}</span>
    {/if}
  </span>
</div>

<style>
  .inline-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .field-label {
    font-size: 0.75rem;
    color: var(--muted, #6b7280);
  }
  .inline-field-control {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    width: 100%;
    min-width: 0;
    min-height: 24px;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .inline-display {
    display: inline-block;
    text-align: left;
    background: transparent;
    border: 1px dashed transparent;
    padding: 2px 6px;
    margin: -2px -6px;
    border-radius: 4px;
    cursor: text;
    font: inherit;
    color: inherit;
    width: 100%;
    min-width: 0;
    min-height: 24px;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .inline-display:hover,
  .inline-display:focus-visible {
    border-color: var(--border, #e3e6eb);
    background: #fafbfd;
    outline: none;
  }
  .multi-edit {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 6px 8px;
    border: 1px solid var(--primary, #2563eb);
    border-radius: 4px;
    background: #fff;
  }
  .chips {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px 12px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.8rem;
  }
  .chip input[type='checkbox'] {
    margin: 0;
  }
  /* compact: 常時表示の候補 chip。 panel の枠を持たず、 選択済みを淡くハイライト。 */
  .chips-compact {
    width: 100%;
    min-width: 0;
    gap: 4px 10px;
  }
  .chips-compact .chip {
    padding: 1px 8px;
    border: 1px solid var(--border, #d1d5db);
    border-radius: 9999px;
    background: #fff;
    color: var(--muted, #6b7280);
    cursor: pointer;
  }
  .chips-compact .chip.chip-selected {
    border-color: var(--primary, #2563eb);
    background: #eff6ff;
    color: #1e3a8a;
    font-weight: 700;
  }
  /* large: 選択済み chip を表示するボタン。 `選択 ▾` で multi-select と分かる。 */
  .inline-display.ms-large {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    border-color: var(--border, #e3e6eb);
    cursor: pointer;
  }
  .ms-static-chips {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px 6px;
    min-width: 0;
  }
  .ms-static-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border: 1px solid var(--border, #e5e7eb);
    border-radius: 9999px;
    background: #f9fafb;
    font-size: 0.8rem;
  }
  .ms-select-hint {
    flex: 0 0 auto;
    color: var(--muted, #6b7280);
    font-size: 0.7rem;
    white-space: nowrap;
  }
  .muted {
    color: var(--muted, #6b7280);
  }
  .small {
    font-size: 11px;
  }
  .saved {
    color: var(--ok, #10b981);
  }
  .error-msg {
    color: var(--danger, #dc2626);
  }
  .status-note {
    flex: 0 0 auto;
  }
  .saving {
    opacity: 0.85;
  }
  .inline-field.locked {
    opacity: 0.7;
  }
  .inline-field.disabled .inline-display {
    color: var(--muted, #6b7280);
    cursor: not-allowed;
  }
  .lock-mark {
    font-size: 11px;
    color: var(--muted, #6b7280);
    flex: 0 0 auto;
  }
</style>
